import { useState, useEffect } from "react";
import { isSameDay } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import useAuth from "@/hooks/useAuth";
import {
  getHolidays,
  createHolidays,
  updateHolidays,
  deleteHolidays,
} from "@/api/new-api/holidays";
import Loading from "@/components/common/loading";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { DeleteConfirmDialog } from "@/components/dialog/common-dialog";
import { toast } from "react-hot-toast";

const formatDateLocal = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

function HolidayDialog({
  open,
  onClose,
  date,
  holiday,
  onSave,
  deleteHoliday,
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (holiday) {
      setTitle(holiday?.title || "");
      setDescription(holiday?.description || "");
    } else {
      setTitle("");
      setDescription("");
    }
  }, [holiday]);

  const handleSave = () => {
    onSave({
      date,
      title,
      description,
      id: holiday?.id || null,
    });
    onClose();
  };

  const handleConfirmDelete = async () => {
    if (!holiday?.id) return;
    setDeleting(true);
    try {
      await deleteHoliday(holiday?.id);
      setIsConfirmOpen(false);
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md bg-cblack4 text-white border-neutral-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              {holiday ? "Update Holiday" : "Add Holiday"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col space-y-1.5 gap-2">
            <div>
              <Label className="text-neutral-300">Date</Label>
              <Input
                value={formatDateLocal(date)}
                disabled
                className="bg-cblack3 border-neutral-700 text-neutral-400"
              />
            </div>
            <div>
              <Label className="text-neutral-300">Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="bg-cblack3 border-neutral-700 text-white"
                placeholder="Enter holiday title"
              />
            </div>
            <Label className="text-neutral-300">Description</Label>
            <div>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="bg-cblack3 border-neutral-700 text-white"
                placeholder="Enter holiday description"
              />
            </div>
          </div>
          <DialogFooter className="flex justify-end mt-3 gap-2">
            {holiday && (
              <Button
                type="button"
                variant="destructive"
                onClick={() => setIsConfirmOpen(true)}
                className="bg-cred text-white hover:bg-red-700"
              >
                Remove
              </Button>
            )}
            <Button
              type="button"
              onClick={handleSave}
              className="bg-cyellow text-black hover:bg-yellow-500 font-medium"
            >
              {holiday ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Remove Holiday"
        description={`Are you sure you want to remove "${holiday?.title || 'this holiday'}"? This action cannot be undone.`}
        yesBtnTxt="Yes, Remove"
        noBtnTxt="Cancel"
        isLoading={deleting}
      />
    </>
  );
}

function HolidaysCalendar({ holidays, year, onSave, deleteHoliday }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);

  const handleDayClick = (date) => {
    const existingHoliday = holidays.find((h) =>
      isSameDay(new Date(h.date), date)
    );
    setSelectedDate(date);
    setEditingHoliday(existingHoliday || null);
    setDialogOpen(true);
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {[...Array(12).keys()].map((month) => (
          <Card key={month} className="bg-cblack5 text-white p-2">
            <TooltipProvider>
              <Calendar
                mode="single"
                selected={selectedDate}
                month={new Date(year, month, 1)}
                className="rounded-md bg-transparent"
                modifiers={{
                  holiday: (date) =>
                    holidays.some((h) => isSameDay(new Date(h.date), date)),
                }}
                modifiersClassNames={{
                  holiday: "bg-red-600 text-white hover:bg-red-500",
                }}
                onDayClick={handleDayClick}
                renderDay={(date) => {
                  const matchedHoliday = holidays.find((h) =>
                    isSameDay(new Date(h.date), date)
                  );
                  if (matchedHoliday) {
                    return (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="w-full h-full flex items-center justify-center cursor-pointer">
                            {date.getDate()}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="z-50 max-w-xs text-left">
                          <p className="font-semibold text-sm">
                            {matchedHoliday?.title}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {matchedHoliday?.description}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    );
                  }
                  return (
                    <div className="w-full h-full flex items-center justify-center cursor-pointer">
                      {date.getDate()}
                    </div>
                  );
                }}
              />
            </TooltipProvider>
          </Card>
        ))}
      </div>

      {dialogOpen && (
        <HolidayDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          date={selectedDate}
          holiday={editingHoliday}
          onSave={onSave}
          deleteHoliday={deleteHoliday}
        />
      )}
    </>
  );
}

export default function HolidaysPage() {
  const { user } = useAuth();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());

  const yearOptions = Array.from({ length: 11 }, (_, i) => {
    const year = 2020 + i;
    return {
      label: year.toString(),
      value: year.toString(),
    };
  });

  const fetchAllHolidays = async () => {
    if (!user?.restaurant_id) return;
    setLoading(true);
    try {
      const response = await getHolidays(user.restaurant_id);
      if (response?.status === "success") setHolidays(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllHolidays();
  }, [user?.restaurant_id]);

  const handleSaveHoliday = async (data) => {
    if (!user?.restaurant_id) return;

    const formattedData = {
      ...data,
      date: data.date instanceof Date ? formatDateLocal(data.date) : data.date,
      restaurant_id: user.restaurant_id,
    };
    try {
      let response;
      if (data?.id) {
        response = await updateHolidays(data?.id, formattedData);
        if (response?.status === "success") {
          setHolidays((prev) =>
            prev.map((h) => (h.id === response.data.id ? response.data : h))
          );
          toast.success("Holiday updated successfully");
        } else {
          toast.error(response.message || "Failed to update holiday");
        }
      } else {
        response = await createHolidays(formattedData);
        if (response?.status === "success") {
          setHolidays((prev) => [...prev, response.data]);
          toast.success("Holiday created successfully");
        } else {
          toast.error(response.message || "Failed to create holiday");
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong!");
    }
  };

  const deleteHoliday = async (id) => {
    if (!id) return;
    try {
      const data = await deleteHolidays(id);
      console.log(data, "lajdfklsfjslf");
      if (data?.status === "success") {
        setHolidays((prev) => prev.filter((h) => h.id !== id));
        toast.success("Holiday deleted successfully");
      } else {
        toast.error(data?.message || "Failed to delete holiday");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong while deleting!");
    }
  };

  if (loading) return <Loading links={backofficeLinks} />;

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
      pageName={{ title: "Holidays" }}
      paths={[
        { name: "Back Office", path: "/backoffice/overview" },
        {
          name: "Staff Management",
          path: "/backoffice/staff-management/staff/all",
        },
        {
          name: "Holidays",
          path: "/backoffice/staff-management/holidays",
        },
      ]}
    >
      <div className="container mx-auto p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="w-32">
            <Select
              value={String(year)}
              onValueChange={(val) => setYear(Number(val))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {yearOptions?.map((y) => (
                  <SelectItem key={y.value} value={String(y.value)}>
                    {y.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm text-cwhite font-medium">
            <span className="text-[12px]">🔴</span> Holidays
          </div>
        </div>

        <HolidaysCalendar
          holidays={holidays}
          year={year}
          onSave={handleSaveHoliday}
          deleteHoliday={(id) => deleteHoliday(id)}
        />
      </div>
    </Layout2>
  );
}
